/**
 * What we read from a link a shopper attached to the search bar.
 *
 * Built by shared/src/server/links/resolve-link-preview.ts, served to the
 * search bar by POST /api/link-preview (chip + thread card) and folded into
 * the conversation prompt by /api/product-search. Amazon is read through the
 * Creators API or our own catalogue, never by fetching an Amazon page.
 *
 * `excerpt` (up to 2,500 chars of page text) is for the model only: the
 * route strips it before anything reaches a browser (toWirePreview).
 */

interface LinkPreviewBase {
  /** The normalized address (shared/src/utils/link-url.ts). */
  url: string;
  /** Lowercase host without www. */
  host: string;
  /** ISO-8601 time the preview was resolved. */
  resolvedAt: string;
}

export interface AmazonLinkPreview extends LinkPreviewBase {
  kind: "amazon";
  asin: string;
  marketplace: { domain: string; regionCode: string };
  /** The link's store is not the shopper's store (amazon.com on en-CA). */
  crossStore: boolean;
  /** https://www.<domain>/dp/<ASIN>; null where Amazon links are suppressed (the app). */
  cleanUrl: string | null;
  /** Our /go hop to cleanUrl (it adds the partner tag for verified in-market humans). */
  outboundHref: string | null;
  title: string | null;
  brand: string | null;
  image: string | null;
  category: string | null;
  /** Up to 5 listing bullets, 200 chars each. */
  features: string[];
  /** Only ever with the time Amazon's API gave it (Associates licence). */
  price: { amount: number; currency: string; observedAt: string } | null;
  /** The listing is a renewed / refurbished / used unit. */
  usedUnit: boolean;
  /** Where the identity came from: Amazon's API, our catalogue, or the address's own words. */
  source: "creators" | "catalog" | "slug";
  note?: "no-listing" | "no-credential" | "quota" | "lookup-failed";
}

export interface PageLinkPreview extends LinkPreviewBase {
  /** "product" when the page describes one product (JSON-LD, og:type or a structured price). */
  kind: "product" | "page";
  title: string | null;
  brand: string | null;
  image: string | null;
  description: string | null;
  siteName: string | null;
  canonical: string | null;
  /** A structured price the page published (JSON-LD / OpenGraph / microdata), never one scraped from prose. */
  price: { amount: number; currency: string | null; observedAt: string } | null;
  identifiers: { sku?: string; gtin?: string; mpn?: string };
  availability: string | null;
  /** Up to 8 feature bullets, 200 chars each. */
  bullets: string[];
  /** Server only: page text for the model. Never sent to a browser. */
  excerpt?: string;
}

export type UnavailableLinkReason =
  | "invalid-url"
  | "unsafe-url"
  | "dns"
  | "private"
  | "too-many-redirects"
  | "timeout"
  | "not-html"
  | "blocked"
  | "http-error"
  | "fetch-error"
  | "amazon-other";

export interface UnavailableLinkPreview extends LinkPreviewBase {
  kind: "unavailable";
  reason: UnavailableLinkReason;
  status?: number;
  /** The words the address itself carries ("garmin fenix 8"), possibly "". */
  slugWords: string;
}

export type LinkPreview = AmazonLinkPreview | PageLinkPreview | UnavailableLinkPreview;

/** The preview as a browser may see it: no page excerpt. */
export function toWirePreview(preview: LinkPreview): LinkPreview {
  if (preview.kind === "product" || preview.kind === "page") {
    const wire = { ...preview };
    delete wire.excerpt;
    return wire;
  }
  return preview;
}

/** A loose shape check for previews read back from a cache or a response. */
export function isLinkPreview(value: unknown): value is LinkPreview {
  if (!value || typeof value !== "object") return false;
  const v = value as Partial<LinkPreview>;
  return (
    typeof v.url === "string" &&
    typeof v.host === "string" &&
    (v.kind === "amazon" || v.kind === "product" || v.kind === "page" || v.kind === "unavailable")
  );
}
